/**
 * Settings → Operations pilot card — shared UI for a {@link PilotFlagHandle}:
 * who is in the pilot, "Enable for everyone" cutover, last-changed trail.
 */
import React, { useEffect, useState } from 'react';
import { FlaskConical, Loader2, Rocket } from 'lucide-react';
import type { EmployeeDesignation, UserRole } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { EMPTY_PILOT_FLAG, type PilotFlag, type PilotFlagHandle } from '../../utils/pilotFlagAccess';
import ReleaseAudiencePicker from '../release/ReleaseAudiencePicker';
import './FilesRedesignPilotCard.css';

function fmt(d: Date | null): string {
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

export interface PilotFlagCardProps {
  flag: PilotFlagHandle;
  title: string;
  /** What the pilot turns on, in one or two sentences. */
  description: string;
  /** Confirmation copy for the cutover switch. */
  enableAllConfirm: string;
  /** Shown once general availability is on (what to clean up). */
  gaNotice: React.ReactNode;
  /** Extra buttons rendered in the action row (e.g. "Reset walkthrough"). */
  extraActions?: React.ReactNode;
  ariaLabel?: string;
}

const PilotFlagCard: React.FC<PilotFlagCardProps> = ({ flag: handle, title, description, enableAllConfirm, gaNotice, extraActions, ariaLabel }) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const isAdmin = user?.role === 'admin';

  const [flag, setFlag] = useState<PilotFlag>(EMPTY_PILOT_FLAG);
  const [loaded, setLoaded] = useState(false);
  const [draftRoles, setDraftRoles] = useState<UserRole[]>([]);
  const [draftDesignations, setDraftDesignations] = useState<EmployeeDesignation[]>([]);
  const [draftUserIds, setDraftUserIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      handle.subscribe(
        next => {
          setFlag(next);
          setDraftRoles(next.pilotRoles);
          setDraftDesignations(next.pilotDesignations);
          setDraftUserIds(next.pilotUserIds);
          setLoaded(true);
        },
        () => setError('Could not load the pilot settings.'),
      ),
    [handle],
  );

  const dirty =
    draftRoles.join('|') !== flag.pilotRoles.join('|')
    || draftDesignations.join('|') !== flag.pilotDesignations.join('|')
    || draftUserIds.join('|') !== flag.pilotUserIds.join('|');

  const actor = { uid: user?.id ?? '', name: user?.name ?? '' };

  const savePilot = async () => {
    if (!isAdmin) return;
    setSaving(true);
    setError(null);
    try {
      await handle.save({ pilotRoles: draftRoles, pilotDesignations: draftDesignations, pilotUserIds: draftUserIds }, actor, flag);
      setStatus('Pilot list saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const setGa = async (on: boolean) => {
    if (!isAdmin) return;
    const ok = window.confirm(on ? enableAllConfirm : 'Turn general availability off and go back to the pilot list?');
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await handle.save({ generalAvailability: on }, actor, flag);
      setStatus(on ? 'Enabled for everyone.' : 'Back to pilot mode.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const pilotNames = flag.pilotUserIds
    .map(id => crmUsers.find(u => u.uid === id)?.displayName || null)
    .filter((n): n is string => !!n);

  return (
    <section className="settings-ops-section frp" aria-label={ariaLabel ?? title}>
      <h4 className="settings-ops-section-title">
        <FlaskConical size={15} aria-hidden style={{ verticalAlign: '-3px', marginRight: 6 }} />
        {title}
      </h4>

      {!loaded ? (
        <p className="setting-description">
          <Loader2 size={13} className="frp-spin" aria-hidden /> Loading…
        </p>
      ) : flag.generalAvailability ? (
        <>
          <p className="frp-ga-notice" role="status">
            <Rocket size={14} aria-hidden />
            <span>
              <strong>Enabled for everyone</strong>
              {flag.generalAvailabilitySince ? ` since ${fmt(flag.generalAvailabilitySince)}` : ''}
              {flag.updatedByName ? ` · by ${flag.updatedByName}` : ''}. {gaNotice}
            </span>
          </p>
          <div className="frp-actions">
            {extraActions}
            {isAdmin ? (
              <button type="button" className="frp-btn frp-btn--ghost" disabled={saving} onClick={() => void setGa(false)}>
                Back to pilot list
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <p className="setting-description">
            {description}
            {flag.updatedAt ? ` Last changed ${fmt(flag.updatedAt)}${flag.updatedByName ? ` by ${flag.updatedByName}` : ''}.` : ''}
          </p>
          <p className="frp-summary">
            {flag.pilotUserIds.length + flag.pilotRoles.length + flag.pilotDesignations.length === 0 ? (
              <span>Nobody is in the pilot yet.</span>
            ) : (
              <>
                <strong>In the pilot:</strong>{' '}
                {[
                  ...flag.pilotRoles.map(r => `all ${r.replace('_', ' ')}s`),
                  ...flag.pilotDesignations.map(d => `all ${d.replace('_', ' ')}s`),
                  ...pilotNames,
                ].join(', ')}
                {flag.pilotUserIds.length > pilotNames.length ? ` (+${flag.pilotUserIds.length - pilotNames.length} not loaded)` : ''}
              </>
            )}
          </p>

          {isAdmin ? (
            <>
              <ReleaseAudiencePicker
                label="Who is in the pilot?"
                includeTestUsers
                includeDesignPartners
                selected={draftRoles}
                selectedDesignations={draftDesignations}
                selectedUserIds={draftUserIds}
                onChange={setDraftRoles}
                onDesignationsChange={setDraftDesignations}
                onUserIdsChange={setDraftUserIds}
                disabled={saving}
              />
              <div className="frp-actions">
                <button type="button" className="frp-btn frp-btn--primary" disabled={saving || !dirty} onClick={() => void savePilot()}>
                  {saving ? 'Saving…' : 'Save pilot list'}
                </button>
                <button type="button" className="frp-btn frp-btn--ghost" disabled={saving} onClick={() => void setGa(true)}>
                  <Rocket size={13} aria-hidden /> Enable for everyone (cutover)
                </button>
                {extraActions}
              </div>
            </>
          ) : extraActions ? (
            <div className="frp-actions">{extraActions}</div>
          ) : null}
        </>
      )}

      {status ? (
        <p className="frp-status" role="status">
          {status}
        </p>
      ) : null}
      {error ? <p className="frp-error">{error}</p> : null}
    </section>
  );
};

export default PilotFlagCard;
